import { Button } from 'components/Button';
import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { OptionField } from 'components/Field/OptionField';
import { TextField } from 'components/Field/TextField';
import { ICONS } from 'components/Icon';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { Page } from 'components/Page';
import { PageHeader } from 'components/PageHeader';
import { SeparatorHorizontal } from 'components/SeparatorHorizontal';
import { useConfirmUnsaved } from 'hooks/useConfirm';
import { useEsc } from 'hooks/useEsc';
import { useSelector } from 'hooks/useSelector';
import { isEqual, sortBy } from 'lodash';
import React from 'react';

import { useDispatch } from 'react-redux';
import {
  doAsesListClearOrganisations,
  doAsesListFilterOpenOrganisationSelect,
  doAsesListLoad,
  doAsesListSetFilterDraft,
  NAVIGATE_MODAL_BACK,
} from 'store/actions';
import {
  selectAsesListFilter,
  selectAsesListFilterDraft,
  selectAsesListFilterOptionsDraft,
} from 'store/ases/selectors';
import { AsesDefaultListFilter } from 'store/ases/types';
import { AssessmentStatusesEnum } from 'types/types';

export const AssessmentListFilter: React.FC<
  ModalScreenProps<ModalScreens.AsesListFilter>
> = ({ navigation }) => {
  const dispatch = useDispatch();
  const confirmUnsaved = useConfirmUnsaved();

  const searchFieldKey = React.useRef(0);

  const filterOptions = useSelector(selectAsesListFilterOptionsDraft);

  const filter = useSelector(selectAsesListFilter);
  const filterDraft = useSelector(selectAsesListFilterDraft);
  const organisationBlocker = filterOptions.organisationRequired;

  const setFilterState = React.useCallback(
    (newFilter: Partial<typeof filterDraft>) => {
      dispatch(doAsesListSetFilterDraft({ data: newFilter, append: true }));
    },
    [dispatch],
  );

  const handleSearchChange = (newSearchValue: string) =>
    setFilterState({ searchTerm: newSearchValue });

  const handleFilter = React.useCallback(() => {
    dispatch(
      doAsesListLoad({
        type: 'filter',
        useDraftFilter: true,
        useBlockingLoader: true,
      }),
    );
  }, [dispatch]);

  const handleSetStatuses = React.useCallback(
    (statusIds: AssessmentStatusesEnum[]) =>
      setFilterState({
        statuses: statusIds,
      }),
    [setFilterState],
  );

  const handleSetAssetGroup = React.useCallback(
    (assetGroupIds: string[]) =>
      setFilterState({
        propertyGroupIds: assetGroupIds,
      }),
    [setFilterState],
  );

  const handleSetAssessmentType = React.useCallback(
    (assessmentType: string[]) =>
      setFilterState({
        assessmentTypes: assessmentType,
      }),
    [setFilterState],
  );

  const handleSetFlagged = React.useCallback(
    (flagIds: string[]) =>
      setFilterState({
        flagsSet: flagIds,
      }),
    [setFilterState],
  );

  const handleSetNotFlagged = React.useCallback(
    (flagIds: string[]) =>
      setFilterState({
        flagsNotSet: flagIds,
      }),
    [setFilterState],
  );

  const handleSetOrderBy = React.useCallback(
    (orderBy: string) =>
      setFilterState({
        orderBy,
      }),
    [setFilterState],
  );

  const isDirty = !isEqual(filter, filterDraft);

  const handleClose = () => {
    if (isDirty) {
      confirmUnsaved({
        onSave: handleFilter,
        onDiscard: () => dispatch(NAVIGATE_MODAL_BACK()),
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  };

  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !isDirty });
  }, [isDirty, navigation]);
  useEsc(handleClose);

  return (
    <Page
      scrollView
      header={
        <PageHeader
          title="Filter"
          subtitle="Assessments"
          onPressCancelButton={handleClose}
          onPressSaveButton={organisationBlocker ? null : handleFilter}
        />
      }
    >
      <Card
        explanation={
          organisationBlocker
            ? 'Choose at least one organisation to see the available filters'
            : 'Changing the organisations may affect which other filters are available to you below'
        }
      >
        <CardRow
          label={
            'Organisations (' +
            (filterDraft.organisationIds.length
              ? `${filterDraft.organisationIds.length} / ${filterOptions.organisations.length}`
              : `${filterOptions.organisations.length}`) +
            ')'
          }
          value={sortBy(filterOptions.organisations, 'name')
            .reduce<string[]>((acc, org) => {
              if (filterDraft.organisationIds.includes(org.id)) {
                acc.push(org.name);
              }
              return acc;
            }, [])
            .join(', ')}
          placeholder="All Organisations"
          labelAlignTop
          labelNumberOfLines={0}
          onPress={() =>
            dispatch(
              doAsesListFilterOpenOrganisationSelect({
                getDraft: true,
                openFilterAfterSelect: false,
              }),
            )
          }
          actionIcon={ICONS.CircleXmarkLight}
          onAction={
            filterDraft.organisationIds.length
              ? () => dispatch(doAsesListClearOrganisations())
              : undefined
          }
          showBadge={!!filterDraft.organisationIds.length}
        />
      </Card>

      {!organisationBlocker && <SeparatorHorizontal></SeparatorHorizontal>}

      {!organisationBlocker && (
        <Card
          explanation={`Try searching 'Fire Risk Assessment', a property address, an assessor name...`}
        >
          <TextField
            iconLeft={ICONS.MagnifyingGlassLight}
            key={searchFieldKey.current}
            value={filterDraft.searchTerm}
            onChange={handleSearchChange}
            placeholder="Search"
            onEsc={handleClose}
            autoFocusWeb={searchFieldKey.current === 0}
            autoFocusDelay
            clearable
            autoCorrect={false}
            autoComplete="off"
            autoCapitalize="none"
          />
        </Card>
      )}

      {!organisationBlocker && (
        <Card>
          <OptionField
            label="Status"
            modalLabel="Statuses"
            onChange={handleSetStatuses}
            value={filterDraft.statuses}
            noValueOption={{
              name: 'All Statuses',
            }}
            showBadge={!!filterDraft.statuses.length}
            options={filterOptions.statuses.map((status) => ({
              name: status.name,
              iconColor: `#${status.colour}`,
              internalValue: status.id,
              icon: ICONS.FileDuotone,
            }))}
            showLabelInCloseHeader
            labelAlignTop
            expandedInlineFirst
            multiSelect
            modalSize="small"
            modalSearchable
            clearable
            modalShowQuickChoose={true}
            showFieldTypeIcon={false}
          />
          {filterOptions.assessmentTypes.length > 1 && (
            <OptionField
              label="Type"
              modalLabel="Type"
              onChange={handleSetAssessmentType}
              value={filterDraft.assessmentTypes}
              noValueOption={{
                name: 'All Types',
              }}
              showBadge={!!filterDraft.assessmentTypes.length}
              options={sortBy(filterOptions.assessmentTypes, 'name').map(
                (item) => ({
                  name: item.name,
                  internalValue: item.id,
                }),
              )}
              forceModal
              labelAlignTop
              multiSelect
              modalSize="small"
              modalSearchable
              clearable
              modalShowQuickChoose={true}
              showFieldTypeIcon={false}
            />
          )}
          {filterOptions.propertyGroups.length > 1 && (
            <OptionField
              label="Property Group"
              modalLabel="Property Group"
              onChange={handleSetAssetGroup}
              value={filterDraft.propertyGroupIds}
              noValueOption={{
                name: 'All Property Groups',
              }}
              showBadge={!!filterDraft.propertyGroupIds.length}
              options={filterOptions.propertyGroups.map((assetGroup) => ({
                name: assetGroup.name,
                internalValue: assetGroup.id,
                groupName: assetGroup.organisationName,
              }))}
              forceModal
              labelAlignTop
              multiSelect
              modalSize="small"
              modalSearchable
              clearable
              modalShowQuickChoose={true}
              showFieldTypeIcon={false}
            />
          )}
        </Card>
      )}

      {!!filterOptions.flags.length && (
        <Card>
          <OptionField
            label="Flagged"
            modalLabel="Flagged"
            labelAlignTop
            showLabelInCloseHeader
            labelStyle={{ minWidth: 90 }}
            valueNumberOfLinesWithLabel={0}
            expandedInlineFirst
            clearable
            showFieldTypeIcon={false}
            onChange={(values) => handleSetFlagged(values)}
            value={filterDraft.flagsSet}
            showBadge={!!filterDraft.flagsSet.length}
            multiSelect
            modalSize="small"
            modalSearchable
            noValueOption={{
              name: 'None',
            }}
            options={filterOptions.flags.map((flag) => ({
              name: flag.name,
              internalValue: flag.id,
            }))}
          />
          <OptionField
            label="Not Flagged"
            modalLabel="Not Flagged"
            labelAlignTop
            showLabelInCloseHeader
            labelStyle={{ minWidth: 90 }}
            valueNumberOfLinesWithLabel={0}
            expandedInlineLast
            clearable
            showFieldTypeIcon={false}
            onChange={(values) => handleSetNotFlagged(values)}
            value={filterDraft.flagsNotSet}
            showBadge={!!filterDraft.flagsNotSet.length}
            multiSelect
            modalSize="small"
            modalSearchable
            noValueOption={{
              name: 'None',
            }}
            options={filterOptions.flags.map((flag) => ({
              name: flag.name,
              internalValue: flag.id,
            }))}
          />
        </Card>
      )}

      {!organisationBlocker && (
        <Card>
          <OptionField
            showLabelInCloseHeader
            showCloseHeaderSingleSelect
            modalLabel="Order By"
            expandedInlineFirst
            expandedInlineLast
            onChange={(values) => handleSetOrderBy(values[0]!)}
            value={[filterDraft.orderBy]}
            label="Order By"
            labelAlignTop
            options={[
              {
                name: 'Assessed Date (Newest First)',
                internalValue: 'assessedDate:desc',
              },
              {
                name: 'Assessed Date (Oldest First)',
                internalValue: 'assessedDate:asc',
              },
              {
                name: 'Approved Date (Newest First)',
                internalValue: 'approvedDate:desc',
              },
              {
                name: 'Approved Date (Oldest First)',
                internalValue: 'approvedDate:asc',
              },
            ]}
          />
        </Card>
      )}

      {!organisationBlocker && (
        <Button
          text="Clear Filters"
          style={{
            alignSelf: 'center',
          }}
          onPress={() => {
            searchFieldKey.current += 1;
            setFilterState({
              ...AsesDefaultListFilter,
              organisationIds: filterDraft.organisationIds,
              orderBy: filterDraft.orderBy,
            });
          }}
          disabled={
            filterDraft.searchTerm === '' &&
            !filterDraft.statuses.length &&
            !filterDraft.propertyGroupIds.length &&
            !filterDraft.flagsSet.length &&
            !filterDraft.flagsNotSet.length
          }
        />
      )}
    </Page>
  );
};
